import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";

const MOOD = { romantic: "#EF4444", emotional: "#8B5CF6", celebratory: "#F59E0B", joyful: "#E6B04E", solemn: "#00F0FF", candid: "#10B981", neutral: "#64748B" };

export default function Story() {
  const { projectId } = useApp();
  const [busy, setBusy] = useState("");
  const navigate = useNavigate();
  const { data, refresh } = usePoll(() => (projectId ? api.getStory(projectId) : Promise.resolve(null)), 5000, [projectId]);
  if (!projectId) return <NoProject title="Story needs a project" />;
  const chapters = data?.chapters || [];

  const gen = async () => {
    setBusy("story");
    try { await api.generateStory(projectId); toast.success("Story generated"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed — run Wedding AI to tag events first"); }
    finally { setBusy(""); }
  };
  const genPlan = async () => {
    setBusy("plan");
    try { await api.generateEditPlan(projectId); toast.success("Edit plan built"); navigate("/timeline"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="p-8 fade-up space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#212534] flex items-center justify-center"><BookOpen size={22} className="text-[#E6B04E]" /></div>
          <div>
            <h1 className="font-head text-3xl font-extrabold tracking-tight">Wedding Story</h1>
            <p className="text-[#94A3B8] text-sm">AI-generated narrative from detected events, people &amp; style {data?.provider ? `· via ${data.provider}` : ""}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-ghost" onClick={gen} disabled={!!busy} data-testid="btn-generate-story">
            {busy === "story" ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {chapters.length ? "Regenerate" : "Generate Story"}
          </button>
          {chapters.length > 0 && (
            <button className="btn btn-gold" onClick={genPlan} disabled={!!busy} data-testid="btn-generate-plan">
              {busy === "plan" ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Build Edit Plan
            </button>
          )}
        </div>
      </div>

      {chapters.length === 0 ? (
        <div className="studio-card p-12 text-center text-[#64748B]">No story yet. Run <b>Wedding AI</b> to tag events, then Generate Story.</div>
      ) : (
        <div className="space-y-4">
          {chapters.map((ch, i) => (
            <div key={i} className="studio-card p-5" data-testid={`story-chapter-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-7 h-7 rounded-lg bg-[#E6B04E]/15 text-[#E6B04E] font-head font-bold flex items-center justify-center text-sm">{i + 1}</span>
                <h3 className="font-head font-bold text-lg">{ch.title}</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-10">
                {(ch.scenes || []).map((s, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: MOOD[s.mood] || "#64748B" }} />
                    <span className="text-[#CBD5E1]">{s.title}</span>
                    <span className="tech-badge ml-auto">{s.event}</span>
                    {s.continuous && <span className="tech-badge" style={{ color: "#00F0FF" }}>continuous</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
